import ReactDOM from "react-dom/client";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import MainRouter from "./routes";
import "./assets/style/global.scss";
import * as Sentry from "@sentry/react";
import { io } from "socket.io-client";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const socket = io(process.env.REACT_APP_PUBLIC_URL, {
  path:
    process.env.REACT_APP_NODE_ENV === "local"
      ? "/socket.io/"
      : "/api/socket.io/",
  transports: ["websocket", "polling"],
  polling: {
    interval: 30000,
  },
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  timeout: 20000,
  auth: {
    admin_key: process.env.REACT_APP_SOCKET_ADMIN_KEY || undefined,
  },
});

// IDOR Protection: Set user credentials for authorization
// Call this after user data is fetched to enable authorization checks
// Returns a promise that resolves when credentials are updated on server
socket.setUserCredentials = (userUuid, organizationId) => {
  return new Promise((resolve) => {
    // Only update if credentials have actually changed
    const currentUserUuid = socket.auth?.user_uuid;
    const currentOrgId = socket.auth?.organization_id;
    
    if (currentUserUuid === userUuid && currentOrgId === organizationId) {
      // Credentials unchanged, no need to update
      resolve();
      return;
    }
    
    // Update auth for future reconnections
    socket.auth = {
      ...socket.auth,
      user_uuid: userUuid,
      organization_id: organizationId,
    };
    
    // Update server-side socket credentials immediately (without reconnect)
    if (socket.connected) {
      socket.emit("update-socket-credentials", {
        user_uuid: userUuid,
        organization_id: organizationId,
      });
      // Wait for server acknowledgment
      const handler = (response) => {
        socket.off("update-socket-credentials", handler);
        resolve();
      };
      socket.on("update-socket-credentials", handler);
      // Timeout fallback in case server doesn't respond
      setTimeout(() => {
        socket.off("update-socket-credentials", handler);
        resolve();
      }, 1000);
    } else {
      resolve();
    }
  });
};

// Clear user credentials on logout
socket.clearUserCredentials = () => {
  socket.auth = {
    admin_key: process.env.REACT_APP_SOCKET_ADMIN_KEY || undefined,
  };
};

// Browser verification state
let isVerified = false;
let verificationPromiseResolve = null;
let verificationPromise = new Promise((resolve) => {
  verificationPromiseResolve = resolve;
});

// Store original emit function
const originalEmit = socket.emit.bind(socket);

// Events allowed before verification (challenge-response flow)
const ALLOWED_BEFORE_VERIFICATION = ["browser-challenge-response"];

// Override emit to auto-wait for verification
socket.emit = function (event, ...args) {
  // Allow challenge-response events immediately
  if (ALLOWED_BEFORE_VERIFICATION.includes(event)) {
    return originalEmit(event, ...args);
  }
  // If already verified, emit immediately
  if (isVerified) {
    return originalEmit(event, ...args);
  }
  // Wait for verification then emit
  verificationPromise.then(() => {
    originalEmit(event, ...args);
  });
};

// Check if already verified
socket.isVerified = () => isVerified;

// Wait for verification
socket.waitForVerification = () => verificationPromise;

// Browser verification challenge handlers
socket.on("browser-challenge", (challenge) => {
  const answer = challenge.a * challenge.b + (challenge.timestamp % 1000);
  originalEmit("browser-challenge-response", {
    challengeId: challenge.challengeId,
    answer,
  });
});

socket.on("browser-verification-required", (data) => {
  const answer =
    data.challenge.a * data.challenge.b + (data.challenge.timestamp % 1000);
  originalEmit("browser-challenge-response", {
    challengeId: data.challenge.challengeId,
    answer,
  });
});

socket.on("browser-verification-failed", (data) => {
  const answer =
    data.newChallenge.a * data.newChallenge.b +
    (data.newChallenge.timestamp % 1000);
  originalEmit("browser-challenge-response", {
    challengeId: data.newChallenge.challengeId,
    answer,
  });
});

socket.on("browser-verified", () => {
  isVerified = true;
  if (verificationPromiseResolve) {
    verificationPromiseResolve();
  }
});

// Reset verification state on disconnect
socket.on("disconnect", () => {
  isVerified = false;
  // Create new promise for next connection
  verificationPromise = new Promise((resolve) => {
    verificationPromiseResolve = resolve;
  });
});

const queryClient = new QueryClient();

if (["production", "staging"].includes(process.env.REACT_APP_NODE_ENV)) {
  Sentry.init({
    dsn: process.env.REACT_APP_PUBLIC_SENTRY_KEY,
    environment: process.env.REACT_APP_NODE_ENV || "staging",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    beforeSend(event) {
      if (!["production", "staging"].includes(process.env.REACT_APP_NODE_ENV)) {
        return null;
      }
      return event;
    },
    // Tracing
    debug: false,
    tracesSampleRate: 0,
    tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
const FallbackComponent = () => <div>Something went wrong...</div>;

root.render(
  <Sentry.ErrorBoundary fallback={<FallbackComponent />}>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <MainRouter socket={socket} />
        <ToastContainer
          autoClose={4000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </BrowserRouter>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

reportWebVitals();
